import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import {
	PageTreeMacroChildrenQuery,
	type PageTreeMacroChildrenQuery$data,
	type PageTreeMacroChildrenQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroChildrenQuery.ts';

import {
	PageTreeMacroAncestorsQuery,
	type PageTreeMacroAncestorsQuery$data,
	type PageTreeMacroAncestorsQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroAncestorsQuery.ts';

import {
	PageTreeMacroSpaceHomepageQuery,
	type PageTreeMacroSpaceHomepageQuery$data,
	type PageTreeMacroSpaceHomepageQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroSpaceHomepageQuery.ts';

import {
	PageTreeMacroRootLevelQuery,
	type PageTreeMacroRootLevelQuery$data,
	type PageTreeMacroRootLevelQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroRootLevelQuery.ts';

import {
	PageTreeMacroSearchByTitleQuery,
	type PageTreeMacroSearchByTitleQuery$data,
	type PageTreeMacroSearchByTitleQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroSearchByTitleQuery.ts';

import {
	parseMacroParams,
	parseRootType,
	parsePageTitle,
	buildTitleSearchCql,
	resolveRootId,
	needsAncestorExpansion,
	needsRootChildren,
	CHILDREN_PAGE_SIZE,
} from '@confluence/pagetree-macro/entry-points/pageTreeUtils.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

/**
 * Preload handler for the pagetree macro.
 *
 * Preloads the initial queries needed to render the tree:
 * - Root resolution (space homepage, ancestors for @parent)
 * - Root's children (first page)
 * - Ancestor IDs (for auto-expansion)
 *
 * This populates the Apollo cache during SSR so the client
 * hydrates from cache instead of making new network requests.
 */
export const pagetreePreloadHandler: MacroPreloadHandler = {
	extensionKey: 'pagetree',

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const { rootParam, spaceKey } = parseMacroParams(
			node.attrs?.parameters?.macroParams,
			context.spaceKey,
		);
		const rootType = parseRootType(rootParam);

		if (!spaceKey) {
			return;
		}

		// Step 1: Resolve root via the appropriate query
		let homepageData: PageTreeMacroSpaceHomepageQuery$data | null | undefined;
		let ancestorData: PageTreeMacroAncestorsQuery$data | null | undefined;
		let searchData: PageTreeMacroSearchByTitleQuery$data | null | undefined;

		switch (rootType) {
			case 'self':
				// No resolution query needed — rootId is contentId
				break;
			case 'title': {
				if (rootParam) {
					const pageTitle = parsePageTitle(rootParam);
					const cql = buildTitleSearchCql(pageTitle, spaceKey);
					const searchResult = await query<
						PageTreeMacroSearchByTitleQuery$data,
						PageTreeMacroSearchByTitleQuery$variables
					>({
						query: PageTreeMacroSearchByTitleQuery,
						variables: { cql },
						context: { single: true },
					});
					searchData = searchResult?.data;
				}
				break;
			}
			case 'parent': {
				const ancestorResult = await preloadAncestors(context.contentId);
				ancestorData = ancestorResult?.data;
				break;
			}
			case 'home': {
				const homepageResult = await query<
					PageTreeMacroSpaceHomepageQuery$data,
					PageTreeMacroSpaceHomepageQuery$variables
				>({
					query: PageTreeMacroSpaceHomepageQuery,
					variables: { spaceKey },
					context: { single: true },
				});
				homepageData = homepageResult?.data;
				break;
			}
			case 'none':
				await query<PageTreeMacroRootLevelQuery$data, PageTreeMacroRootLevelQuery$variables>({
					query: PageTreeMacroRootLevelQuery,
					variables: { spaceKey },
					context: { single: true },
				});
				break;
		}

		// Derive rootId and preload its children
		const rootId = resolveRootId({
			rootType,
			contentId: context.contentId,
			homepageData,
			ancestorData,
			searchData,
		});
		if (rootId && needsRootChildren(rootType)) {
			await preloadChildren(rootId);
		}

		// Step 2: Preload ancestor path for auto-expansion
		// Skip for @self (current page is root, no ancestors to expand)
		// Skip for @parent (current page is direct child of root, no intermediate ancestors)
		if (needsAncestorExpansion(rootType)) {
			const ancestorResult = await preloadAncestors(context.contentId);
			const ancestorNodes = ancestorResult?.data?.ptpage?.nearestAncestors?.nodes;
			if (ancestorNodes) {
				for (const ancestor of ancestorNodes) {
					if (ancestor?.id === rootId) {
						break;
					}
					if (ancestor?.id) {
						await preloadChildren(ancestor.id);
					}
				}
			}
		}
	},
};

/** Preload children for a page */
const preloadChildren = (contentId: string) =>
	query<PageTreeMacroChildrenQuery$data, PageTreeMacroChildrenQuery$variables>({
		query: PageTreeMacroChildrenQuery,
		variables: { contentId, first: CHILDREN_PAGE_SIZE, after: null },
		context: { single: true },
	});

/** Preload ancestor IDs for a page */
const preloadAncestors = (contentId: string) =>
	query<PageTreeMacroAncestorsQuery$data, PageTreeMacroAncestorsQuery$variables>({
		query: PageTreeMacroAncestorsQuery,
		variables: { contentId },
		context: { single: true },
	});
